import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { DealInformationTab } from './types';

type DealInformationTabCounts = Partial<Record<DealInformationTab, number>>;

interface DealInformationTabCountContextValue {
  counts: DealInformationTabCounts;
  setTabCount: (tab: DealInformationTab, count: number | undefined) => void;
}

const DealInformationTabCountContext = createContext<DealInformationTabCountContextValue | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export function DealInformationTabCountProvider({ children }: Props) {
  const [counts, setCounts] = useState<DealInformationTabCounts>({});

  const setTabCount = useCallback((tab: DealInformationTab, count: number | undefined) => {
    setCounts((currentCounts) => {
      const nextCounts = { ...currentCounts };

      if (typeof count === 'number') {
        nextCounts[tab] = count;
      } else {
        delete nextCounts[tab];
      }

      return nextCounts;
    });
  }, []);

  const value = useMemo(() => ({ counts, setTabCount }), [counts, setTabCount]);

  return <DealInformationTabCountContext.Provider value={value}>{children}</DealInformationTabCountContext.Provider>;
}

export function useDealInformationTabCounts() {
  const context = useContext(DealInformationTabCountContext);

  if (!context) {
    throw new Error('useDealInformationTabCounts must be used within DealInformationTabCountProvider.');
  }

  return context.counts;
}

export function useDealInformationTabCount(tab: DealInformationTab, count: number | undefined) {
  const context = useContext(DealInformationTabCountContext);

  if (!context) {
    throw new Error('useDealInformationTabCount must be used within DealInformationTabCountProvider.');
  }

  const { setTabCount } = context;

  useEffect(() => {
    setTabCount(tab, count);
  }, [count, setTabCount, tab]);
}
